import type { CalendarEvent } from '../../types'
import {
  WEEKDAYS_SHORT,
  addDays,
  formatTime,
  isToday,
  parseLocalDateTime,
  startOfWeek,
} from '../../utils/dateUtils'

interface WeekViewProps {
  anchor: Date
  eventsOnDay: (d: Date) => CalendarEvent[]
  onSelectDay: (d: Date) => void
  onSelectEvent: (e: CalendarEvent) => void
}

const HOURS = Array.from({ length: 24 }, (_, i) => i)

export function WeekView({
  anchor,
  eventsOnDay,
  onSelectDay,
  onSelectEvent,
}: WeekViewProps) {
  const weekStart = startOfWeek(anchor)
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i))

  return (
    <div className="week-view">
      <div className="week-view__header">
        <div className="week-view__gutter" />
        {days.map((d) => (
          <button
            key={d.toISOString()}
            type="button"
            className={`week-view__dayhead${isToday(d) ? ' week-view__dayhead--today' : ''}`}
            onClick={() => onSelectDay(d)}
            title="Tagesansicht öffnen"
            aria-label={`${WEEKDAYS_SHORT[(d.getDay() + 6) % 7]} ${d.getDate()}. — Tagesansicht öffnen`}
          >
            <span className="week-view__wd">
              {WEEKDAYS_SHORT[(d.getDay() + 6) % 7]}
            </span>
            <span className="week-view__num">{d.getDate()}</span>
          </button>
        ))}
      </div>

      <div className="week-view__body">
        <div className="week-view__hours">
          {HOURS.map((h) => (
            <div key={h} className="week-view__hour">
              {String(h).padStart(2, '0')}:00
            </div>
          ))}
        </div>
        {days.map((d) => {
          const dayEvents = eventsOnDay(d)
          return (
            <div
              key={d.toISOString()}
              className={`week-view__col${isToday(d) ? ' week-view__col--today' : ''}`}
              onClick={() => onSelectDay(d)}
              onKeyDown={(ev) => {
                if (ev.key === 'Enter' || ev.key === ' ') {
                  ev.preventDefault()
                  onSelectDay(d)
                }
              }}
              role="button"
              tabIndex={0}
              title="Tagesansicht öffnen"
              aria-label={`${d.getDate()}. — Tagesansicht öffnen`}
            >
              {HOURS.map((h) => (
                <div key={h} className="week-view__slot" />
              ))}
              {dayEvents.map((e) => {
                const start = parseLocalDateTime(e.start)
                const end = parseLocalDateTime(e.end)
                const top = ((start.getHours() * 60 + start.getMinutes()) / (24 * 60)) * 100
                const durationMin = Math.max(
                  30,
                  (end.getTime() - start.getTime()) / 60000,
                )
                const height = (durationMin / (24 * 60)) * 100
                return (
                  <button
                    key={e.id}
                    type="button"
                    className="week-event"
                    style={{
                      top: `${top}%`,
                      height: `${Math.min(height, 100 - top)}%`,
                      background: `${e.color}33`,
                      borderColor: e.color,
                      color: e.color,
                    }}
                    onClick={(ev) => {
                      ev.stopPropagation()
                      onSelectEvent(e)
                    }}
                    title={e.title}
                  >
                    <strong>{e.title}</strong>
                    <span>
                      {formatTime(e.start)}–{formatTime(e.end)}
                    </span>
                  </button>
                )
              })}
            </div>
          )
        })}
      </div>
    </div>
  )
}
